// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useIsFocused } from '@react-navigation/native';
import { Stack, useRouter } from 'expo-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { useUser } from '@/app/user-context';
import {
  emitShotTrackingDebug,
  getShotTrackingDebugHistory,
  subscribeShotTrackingDebug,
  type ShotTrackingDebugEvent,
} from '@/features/shot-tracking/debugBus';
import {
  buildPuttCandidates,
  oldestPendingCandidate,
  tallyPuttEstimates,
  type PuttCandidate,
  type PuttVerdict,
} from '@/features/shot-tracking/puttTestAnalysis';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import { getBatteryFraction } from '@/services/deviceSignals';
import {
  isShotTrackingRunning,
  isSupported,
  setPuttTestModeForDebug,
  simulateImpactForDebug,
  startShotTracking,
  stopShotTracking,
} from '@/services/shotTracking';
import { useRoundState } from '@/stores/roundState';
import { useShotTrackingEnabled } from '@/stores/shotTrackingState';
import appColors from '@/theme/colors';

/**
 * Developer-only diagnostics for the automatic shot-tracking pipeline.
 *
 * Every stage reports onto the debug bus and this page renders it live: GPS
 * fixes and the stationarity verdict, microphone windows, raw audio metering,
 * detector impacts, the marks that went out (and whether they were sent or
 * queued), and the fused shots the server broadcast back. Built for walking a
 * real course and watching what the sensors actually said — the calibration
 * data the thresholds were guessed without.
 */

const LOG_LIMIT = 200;
const METER_SAMPLES = 48;

/** dBFS → 0..1 for the meter bars; -80 is silence for this purpose. */
const meterFraction = (db: number) => Math.min(1, Math.max(0, (db + 80) / 80));

const time = (at: number) =>
  new Date(at).toLocaleTimeString(undefined, { hour12: false }) +
  `.${String(at % 1000).padStart(3, '0')}`;

const describe = (event: ShotTrackingDebugEvent): string => {
  switch (event.kind) {
    case 'session':
      return `session ${event.message}`;
    case 'fix':
      return (
        `fix ${event.lat.toFixed(5)},${event.lon.toFixed(5)}` +
        ` ±${event.accuracy?.toFixed(0) ?? '?'}m` +
        ` v=${event.speed?.toFixed(1) ?? '?'}m/s` +
        ` ${event.stationary ? 'STILL' : 'moving'}${event.listening ? ' · listening' : ''}`
      );
    case 'mic':
      return `mic ${event.open ? 'OPEN' : 'closed'}`;
    case 'metering':
      return `metering ${event.db.toFixed(1)} dB`;
    case 'impact':
      return `IMPACT rise ${event.riseDb.toFixed(1)} dB`;
    case 'nearMiss':
      return `near-miss rise ${event.riseDb.toFixed(1)} dB (below threshold)`;
    case 'mark':
      return (
        `${event.markKind === 'putt' ? 'putt mark' : 'mark'} hole ${event.holeNumber ?? '?'} ` +
        (event.lat !== null && event.lon !== null
          ? `${event.lat.toFixed(5)},${event.lon.toFixed(5)} ±${event.accuracyMeters?.toFixed(0) ?? '?'}m`
          : 'no position')
      );
    case 'send':
      return `send ${event.outcome} (${event.markId.slice(0, 8)})`;
    case 'wearPeak':
      return (
        `wrist peak ${event.magnitudeMps2.toFixed(1)} m/s²` +
        (event.rotationRadS !== undefined ? ` · rot ${event.rotationRadS.toFixed(1)} rad/s` : '')
      );
    case 'server':
      return (
        `server hole ${event.hole}: ` +
        event.shots
          .map((shot) =>
            `#${shot.shotNumber} ${shot.distanceMeters !== null ? `${shot.distanceMeters}m` : '—'} ${shot.confidence}`
          )
          .join(', ') +
        (event.putts ? ` · ${event.putts} putts` : '')
      );
  }
};

const eventColor = (kind: ShotTrackingDebugEvent['kind']): string => {
  switch (kind) {
    case 'impact':
      return appColors.accent.gold;
    case 'nearMiss':
      return '#c9a34f';
    case 'wearPeak':
      return '#8fd4c7';
    case 'mark':
    case 'send':
      return '#A1D493';
    case 'server':
      return '#8FC7FF';
    case 'session':
      return appColors.text.primary;
    default:
      return appColors.text.secondary;
  }
};

export default function ShotTrackingDebugScreen() {
  const { t } = useI18n();
  const router = useRouter();
  const { user } = useUser();
  const { isDeveloper } = useEffectiveRoles(user);
  const enabled = useShotTrackingEnabled();
  const roundId = useRoundState((state) => state.roundId);
  const hole = useRoundState((state) => state.hole);

  const [events, setEvents] = useState<ShotTrackingDebugEvent[]>(() =>
    getShotTrackingDebugHistory()
  );
  const [running, setRunning] = useState(() => isShotTrackingRunning());
  const [meter, setMeter] = useState<{
    samples: Array<{ db: number; rise: number | null }>;
    last: { db: number; baseline: number | null; rise: number | null } | null;
  }>({ samples: [], last: null });
  const [simulateNote, setSimulateNote] = useState<string | null>(null);
  const [puttTest, setPuttTest] = useState<{
    running: boolean;
    sounds: Array<{ at: number; rise: number; kind: 'impact' | 'nearMiss' }>;
    peaks: Array<{ at: number; magnitude: number; stillnessMs?: number; rotationRadS?: number }>;
    /** Verdicts keyed by candidate.at (stable first-signal time). */
    verdicts: Record<number, PuttVerdict>;
    /** Real putts the detector never proposed a candidate for. */
    missed: number;
    /** Judging-button press times, for tap-noise suppression. */
    uiTaps: number[];
    note: string | null;
  }>({ running: false, sounds: [], peaks: [], verdicts: {}, missed: 0, uiTaps: [], note: null });
  const [sessionNote, setSessionNote] = useState<string | null>(null);
  const meterRef = useRef<Array<{ db: number; rise: number | null }>>([]);
  const lastMeterRef = useRef<{ db: number; baseline: number | null; rise: number | null } | null>(
    null
  );
  const puttTestRunningRef = useRef(false);
  puttTestRunningRef.current = puttTest.running;
  /** True when THIS page started the capture session (no round involved). */
  const ownedSessionRef = useRef(false);
  /** True when the putt test auto-started the session and should stop it. */
  const puttAutoStartedRef = useRef(false);
  const isFocused = useIsFocused();

  /**
   * Starts the capture pipeline with no round: GPS, stationarity gate,
   * microphone windows, detector — everything — with marks logged to the bus
   * but sent nowhere. This is what makes the page usable on a driving range,
   * in the yard, or on the carpet without opening a round first.
   */
  const startTestSession = async (): Promise<boolean> => {
    if (isShotTrackingRunning()) return true;
    const battery = await getBatteryFraction();
    const result = await startShotTracking({
      roundId: 'debug-session',
      playerId: 'debug-session',
      hasConsent: true,
      batteryFraction: battery,
      onMark: (mark) => {
        emitShotTrackingDebug({
          kind: 'send',
          at: Date.now(),
          markId: mark.markId,
          outcome: 'skipped',
        });
      },
    });
    if (result === 'started') {
      ownedSessionRef.current = true;
      setSessionNote(null);
      return true;
    }
    if (result === 'already-running') return true;
    setSessionNote(t('shotTrackingDebug.sessionFailed', { reason: result }));
    return false;
  };

  const stopTestSession = () => {
    if (!ownedSessionRef.current) return;
    ownedSessionRef.current = false;
    void stopShotTracking();
  };

  // Wrist peaks arrive USER-scoped on the app's shared socket — the BFF
  // relays the watch's putt probes to every connection of the same user, so
  // the phone needs no round open. The watch sends through whichever round it
  // has selected; only the phone side went round-free.
  useEffect(() => {
    if (!isDeveloper) return undefined;
    const socket = getSharedBffWebSocket(getApiBaseUrl());
    // The shared socket only dials once it has a token, and only the my-tours
    // screens used to set one — reached directly, this page would otherwise
    // listen on a socket that never connects and wrist peaks would never show.
    socket.setAuthToken(user?.token ?? null);
    const unsubscribe = socket.onMessage((message) => {
      const frame = message as {
        type?: unknown;
        at?: unknown;
        magnitudeMps2?: unknown;
        stillnessMs?: unknown;
        rotationRadS?: unknown;
      };
      if (!frame || frame.type !== 'putt.probe') return;
      if (typeof frame.magnitudeMps2 !== 'number') return;
      const at = typeof frame.at === 'string' ? Date.parse(frame.at) : Number.NaN;
      emitShotTrackingDebug({
        kind: 'wearPeak',
        at: Number.isFinite(at) ? at : Date.now(),
        magnitudeMps2: frame.magnitudeMps2,
        stillnessMs: typeof frame.stillnessMs === 'number' ? frame.stillnessMs : undefined,
        rotationRadS: typeof frame.rotationRadS === 'number' ? frame.rotationRadS : undefined,
      });
    });
    return () => {
      unsubscribe();
    };
    // user?.token in the deps keeps the socket authenticated across refreshes.
  }, [isDeveloper, user?.token]);

  // Losing focus (navigating towards a round, backing out) must free the
  // session singleton: a lingering page-owned session would win the race
  // against the round's own capture and silently swallow its marks.
  useEffect(() => {
    if (isFocused) return;
    if (puttTestRunningRef.current) {
      setPuttTestModeForDebug(false);
      setPuttTest((current) => (current.running ? { ...current, running: false } : current));
    }
    puttAutoStartedRef.current = false;
    stopTestSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  useEffect(() => {
    if (!isDeveloper) return undefined;

    const unsubscribe = subscribeShotTrackingDebug((event) => {
      if (event.kind === 'metering') {
        meterRef.current = [
          ...meterRef.current.slice(-(METER_SAMPLES - 1)),
          { db: event.db, rise: event.rise ?? null },
        ];
        lastMeterRef.current = {
          db: event.db,
          baseline: event.baseline ?? null,
          rise: event.rise ?? null,
        };
        return;
      }
      if (event.kind === 'impact' || event.kind === 'nearMiss') {
        setPuttTest((current) =>
          current.running
            ? {
                ...current,
                sounds: [...current.sounds, { at: event.at, rise: event.riseDb, kind: event.kind }],
              }
            : current
        );
      }
      if (event.kind === 'wearPeak') {
        setPuttTest((current) =>
          current.running
            ? {
                ...current,
                peaks: [
                  ...current.peaks,
                  {
                    at: event.at,
                    magnitude: event.magnitudeMps2,
                    stillnessMs: event.stillnessMs,
                    rotationRadS: event.rotationRadS,
                  },
                ],
              }
            : current
        );
      }
      if (event.kind === 'session' && event.message === 'stopped') {
        setPuttTest((current) => (current.running ? { ...current, running: false } : current));
      }
      setEvents((current) => [...current.slice(-(LOG_LIMIT - 1)), event]);
    });

    // The metering stream arrives at ~10Hz; render it at 4Hz, and use the same
    // tick to keep the session flag honest without an event for every change.
    const timer = setInterval(() => {
      setRunning(isShotTrackingRunning());
      setMeter((current) =>
        current.last === lastMeterRef.current && current.samples.length === meterRef.current.length
          ? current
          : { samples: meterRef.current.slice(), last: lastMeterRef.current }
      );
    }, 250);

    return () => {
      unsubscribe();
      clearInterval(timer);
      // Leaving the page must never leave a forced microphone — or a whole
      // page-owned capture session — behind.
      if (puttTestRunningRef.current) {
        setPuttTestModeForDebug(false);
      }
      if (ownedSessionRef.current) {
        ownedSessionRef.current = false;
        void stopShotTracking();
      }
    };
  }, [isDeveloper]);

  const latestFix = useMemo(
    () => events.findLast((event): event is Extract<ShotTrackingDebugEvent, { kind: 'fix' }> => event.kind === 'fix'),
    [events]
  );
  const micOpen = useMemo(() => {
    const lastMic = events.findLast((event) => event.kind === 'mic');
    return lastMic?.kind === 'mic' ? lastMic.open : false;
  }, [events]);
  const marks = useMemo(
    () =>
      events
        .filter((event): event is Extract<ShotTrackingDebugEvent, { kind: 'mark' }> => event.kind === 'mark')
        .slice(-15)
        .reverse(),
    [events]
  );
  const sendOutcomes = useMemo(() => {
    const byMark = new Map<string, string>();
    for (const event of events) {
      if (event.kind === 'send') byMark.set(event.markId, event.outcome);
    }
    return byMark;
  }, [events]);
  const serverByHole = useMemo(() => {
    const byHole = new Map<number, Extract<ShotTrackingDebugEvent, { kind: 'server' }>>();
    for (const event of events) {
      if (event.kind === 'server') byHole.set(event.hole, event);
    }
    return [...byHole.entries()].sort((a, b) => a[0] - b[0]);
  }, [events]);
  const log = useMemo(() => [...events].reverse(), [events]);

  const simulate = () => {
    const ok = simulateImpactForDebug();
    setSimulateNote(ok ? t('shotTrackingDebug.simulated') : t('shotTrackingDebug.noSession'));
  };

  // Estimation lives in puttTestAnalysis, where the combine window and the
  // tap-noise rule are unit tested: the detector proposes candidates the
  // moment sound/wrist signals arrive, and the developer judges each one.
  const puttCandidates = useMemo(
    () => buildPuttCandidates(puttTest.sounds, puttTest.peaks, puttTest.uiTaps),
    [puttTest.peaks, puttTest.sounds, puttTest.uiTaps]
  );
  const puttTally = useMemo(
    () => tallyPuttEstimates(puttCandidates, puttTest.verdicts, puttTest.missed),
    [puttCandidates, puttTest.missed, puttTest.verdicts]
  );
  const pendingCandidate = useMemo(
    () => oldestPendingCandidate(puttCandidates, puttTest.verdicts),
    [puttCandidates, puttTest.verdicts]
  );

  const togglePuttTest = async () => {
    if (puttTest.running) {
      setPuttTestModeForDebug(false);
      if (puttAutoStartedRef.current) {
        puttAutoStartedRef.current = false;
        stopTestSession();
      }
      setPuttTest((current) => ({ ...current, running: false, note: null }));
      return;
    }
    // No round needed: a missing session is started on the spot. The phone
    // session gates only the audio half — wrist peaks arrive regardless once
    // the watch's putt probe is on, so the test runs even if it fails here.
    const hadSession = isShotTrackingRunning();
    const sessionUp = await startTestSession();
    if (sessionUp && !hadSession) puttAutoStartedRef.current = true;
    const ok = sessionUp && setPuttTestModeForDebug(true);
    setPuttTest({
      running: true,
      sounds: [],
      peaks: [],
      verdicts: {},
      missed: 0,
      uiTaps: [],
      note: ok ? null : t('shotTrackingDebug.noSession'),
    });
  };

  /** Judges the oldest pending estimate; the press time also feeds noise suppression. */
  const judge = (verdict: PuttVerdict) => {
    const now = Date.now();
    setPuttTest((current) => {
      if (!current.running) return current;
      const candidates = buildPuttCandidates(current.sounds, current.peaks, current.uiTaps);
      const pending = oldestPendingCandidate(candidates, current.verdicts);
      if (!pending) return { ...current, uiTaps: [...current.uiTaps, now] };
      return {
        ...current,
        uiTaps: [...current.uiTaps, now],
        verdicts: { ...current.verdicts, [pending.at]: verdict },
      };
    });
  };

  /** "I putted and nothing was proposed" — the false-negative counter. */
  const markMissed = () => {
    setPuttTest((current) =>
      current.running
        ? { ...current, uiTaps: [...current.uiTaps, Date.now()], missed: current.missed + 1 }
        : current
    );
  };

  const candidateText = (candidate: PuttCandidate) => {
    const parts = [
      `putt? (${[...candidate.sources].sort().join('+')})`,
      candidate.bestRiseDb !== null ? `rise +${candidate.bestRiseDb.toFixed(1)} dB` : null,
      candidate.bestMagnitude !== null ? `${candidate.bestMagnitude.toFixed(1)} m/s²` : null,
      // The address signature: a real putt shows seconds here, walking shows ~0.
      candidate.stillnessMs !== null ? `still ${(candidate.stillnessMs / 1000).toFixed(1)}s` : null,
      // The swing signature: a putt rotates slowly, a jolt barely at all.
      candidate.bestRotationRadS !== null
        ? `rot ${candidate.bestRotationRadS.toFixed(1)} rad/s`
        : null,
    ];
    return parts.filter(Boolean).join(' ');
  };

  const close = () => {
    if (router.canGoBack()) router.back();
    else router.replace('/');
  };

  return (
    <>
      <Stack.Screen options={{ title: t('shotTrackingDebug.title'), headerShown: false }} />
      <View style={styles.screen}>
        <View style={styles.header}>
          <Text style={styles.title}>{t('shotTrackingDebug.title')}</Text>
        </View>
        <ScrollView style={styles.scroll} contentContainerStyle={styles.content}>
          {!isDeveloper ? (
            <Text style={styles.notice} testID="shot-debug-forbidden">
              {t('shotTrackingDebug.developersOnly')}
            </Text>
          ) : (
            <>
              <View style={styles.card} testID="shot-debug-status">
                <Text style={styles.cardTitle}>{t('shotTrackingDebug.status')}</Text>
                <StatusRow label="platform" value={`${Platform.OS}${isSupported() ? '' : ' (capture unsupported)'}`} good={isSupported()} />
                <StatusRow label="opt-in" value={enabled ? 'on' : 'off'} good={enabled} />
                <StatusRow label="session" value={running ? 'running' : 'not running'} good={running} />
                <StatusRow label="round" value={roundId ? `${roundId.slice(0, 8)}… · hole ${hole ?? '?'}` : '—'} good={!!roundId} />
                <StatusRow
                  label="gps"
                  value={
                    latestFix
                      ? `±${latestFix.accuracy?.toFixed(0) ?? '?'}m · ${latestFix.stationary ? 'stationary' : 'moving'}`
                      : '—'
                  }
                  good={!!latestFix?.stationary}
                />
                <StatusRow label="mic" value={micOpen ? 'listening' : 'closed'} good={micOpen} />
              </View>

              <View style={styles.card}>
                <Text style={styles.cardTitle}>
                  {t('shotTrackingDebug.audio')}
                  {meter.last !== null
                    ? `  ${meter.last.db.toFixed(1)} dB` +
                      (meter.last.baseline !== null
                        ? ` · baseline ${meter.last.baseline.toFixed(1)}`
                        : '') +
                      (meter.last.rise !== null
                        ? ` · rise ${meter.last.rise >= 0 ? '+' : ''}${meter.last.rise.toFixed(1)}`
                        : '')
                    : ''}
                </Text>
                <View style={styles.meterRow} testID="shot-debug-meter">
                  {meter.samples.length === 0 ? (
                    <Text style={styles.muted}>{t('shotTrackingDebug.noAudio')}</Text>
                  ) : (
                    meter.samples.map((sample, index) => (
                      <View
                        key={index}
                        style={[
                          styles.meterBar,
                          { height: 4 + meterFraction(sample.db) * 36 },
                          sample.rise !== null && sample.rise >= 8 ? styles.meterBarHot : null,
                        ]}
                      />
                    ))
                  )}
                </View>
              </View>

              <View style={styles.card} testID="shot-debug-putt-test">
                <Text style={styles.cardTitle}>{t('shotTrackingDebug.puttTest')}</Text>
                <Text style={styles.muted}>{t('shotTrackingDebug.puttTestHint')}</Text>
                <View style={styles.actionRow}>
                  <Pressable
                    style={styles.action}
                    onPress={() => void togglePuttTest()}
                    testID="shot-debug-putt-toggle"
                  >
                    <Text style={styles.actionText}>
                      {puttTest.running
                        ? t('shotTrackingDebug.puttTestStop')
                        : t('shotTrackingDebug.puttTestStart')}
                    </Text>
                  </Pressable>
                  <Pressable
                    style={[styles.action, !puttTest.running && styles.actionDisabled]}
                    onPress={markMissed}
                    disabled={!puttTest.running}
                    testID="shot-debug-putt-missed"
                  >
                    <Text style={styles.actionText}>{t('shotTrackingDebug.verdictMissed')}</Text>
                  </Pressable>
                </View>
                {puttTest.running ? (
                  <>
                    <Text
                      style={[styles.statusValue, pendingCandidate && styles.tapVerdict]}
                      testID="shot-debug-putt-pending"
                    >
                      {pendingCandidate
                        ? `◆ ${candidateText(pendingCandidate)} — ${t('shotTrackingDebug.judgePrompt')}`
                        : t('shotTrackingDebug.listening')}
                    </Text>
                    <View style={styles.actionRow}>
                      <Pressable
                        style={[styles.action, !pendingCandidate && styles.actionDisabled]}
                        onPress={() => judge('correct')}
                        disabled={!pendingCandidate}
                        testID="shot-debug-putt-correct"
                      >
                        <Text style={styles.actionText}>
                          {t('shotTrackingDebug.verdictCorrect')}
                        </Text>
                      </Pressable>
                      <Pressable
                        style={[styles.action, !pendingCandidate && styles.actionDisabled]}
                        onPress={() => judge('wrong')}
                        disabled={!pendingCandidate}
                        testID="shot-debug-putt-wrong"
                      >
                        <Text style={styles.actionText}>{t('shotTrackingDebug.verdictWrong')}</Text>
                      </Pressable>
                    </View>
                  </>
                ) : null}
                {puttTest.note ? <Text style={styles.muted}>{puttTest.note}</Text> : null}
                {puttCandidates.length > 0 || puttTally.missed > 0 ? (
                  <>
                    <Text style={styles.statusValue} testID="shot-debug-putt-summary">
                      {t('shotTrackingDebug.puttEstimateSummary', {
                        correct: puttTally.correct,
                        wrong: puttTally.wrong,
                        missed: puttTally.missed,
                        pending: puttTally.pending,
                      })}
                    </Text>
                    {puttCandidates.slice(-10).map((candidate) => {
                      const verdict = puttTest.verdicts[candidate.at];
                      return (
                        <Text
                          key={candidate.at}
                          style={[
                            styles.mono,
                            verdict === 'correct' && styles.matchedSignal,
                            verdict === 'wrong' && styles.wrongSignal,
                            candidate.tapNoise && styles.mutedMono,
                            !verdict && !candidate.tapNoise && styles.tapVerdict,
                          ]}
                        >
                          {time(candidate.at)} ◆ {candidateText(candidate)}
                          {candidate.tapNoise
                            ? ' · tap noise (ignored)'
                            : verdict === 'correct'
                              ? ' → ✓'
                              : verdict === 'wrong'
                                ? ' → ✗'
                                : ' → ?'}
                        </Text>
                      );
                    })}
                  </>
                ) : null}
              </View>

              <View style={styles.actionRow}>
                <Pressable
                  style={[
                    styles.action,
                    running && !ownedSessionRef.current && styles.actionDisabled,
                  ]}
                  disabled={running && !ownedSessionRef.current}
                  onPress={() => {
                    if (running) stopTestSession();
                    else void startTestSession();
                  }}
                  testID="shot-debug-session-toggle"
                >
                  <Text style={styles.actionText}>
                    {running
                      ? t('shotTrackingDebug.stopSession')
                      : t('shotTrackingDebug.startSession')}
                  </Text>
                </Pressable>
                <Pressable style={styles.action} onPress={simulate} testID="shot-debug-simulate">
                  <Text style={styles.actionText}>{t('shotTrackingDebug.simulate')}</Text>
                </Pressable>
                <Pressable
                  style={styles.action}
                  onPress={() => setEvents([])}
                  testID="shot-debug-clear"
                >
                  <Text style={styles.actionText}>{t('shotTrackingDebug.clear')}</Text>
                </Pressable>
              </View>
              {sessionNote ? <Text style={styles.muted}>{sessionNote}</Text> : null}
              {simulateNote ? <Text style={styles.muted}>{simulateNote}</Text> : null}

              <View style={styles.card}>
                <Text style={styles.cardTitle}>{t('shotTrackingDebug.marks')}</Text>
                {marks.length === 0 ? (
                  <Text style={styles.muted}>{t('shotTrackingDebug.none')}</Text>
                ) : (
                  marks.map((mark) => (
                    <Text key={mark.markId} style={styles.mono}>
                      {time(mark.at)} {describe(mark)} →{' '}
                      {sendOutcomes.get(mark.markId) ?? '…'}
                    </Text>
                  ))
                )}
              </View>

              <View style={styles.card}>
                <Text style={styles.cardTitle}>{t('shotTrackingDebug.server')}</Text>
                {serverByHole.length === 0 ? (
                  <Text style={styles.muted}>{t('shotTrackingDebug.none')}</Text>
                ) : (
                  serverByHole.map(([holeNumber, event]) => (
                    <Text key={holeNumber} style={styles.mono}>
                      {describe(event)}
                    </Text>
                  ))
                )}
              </View>

              <View style={styles.card}>
                <Text style={styles.cardTitle}>{t('shotTrackingDebug.log')}</Text>
                {log.length === 0 ? (
                  <Text style={styles.muted}>{t('shotTrackingDebug.none')}</Text>
                ) : (
                  log.map((event, index) => (
                    <Text
                      key={`${event.at}-${index}`}
                      style={[styles.mono, { color: eventColor(event.kind) }]}
                    >
                      {time(event.at)} {describe(event)}
                    </Text>
                  ))
                )}
              </View>
            </>
          )}
        </ScrollView>
        <View style={styles.footer}>
          <Pressable style={styles.closeButton} onPress={close}>
            <Text style={styles.closeText}>{t('shotTrackingDebug.close')}</Text>
          </Pressable>
        </View>
      </View>
    </>
  );
}

function StatusRow({ label, value, good }: { label: string; value: string; good: boolean }) {
  return (
    <View style={styles.statusRow}>
      <View style={[styles.dot, { backgroundColor: good ? '#A1D493' : appColors.border.strong }]} />
      <Text style={styles.statusLabel}>{label}</Text>
      <Text style={styles.statusValue}>{value}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: appColors.surface.base },
  header: { paddingHorizontal: 16, paddingTop: 16, paddingBottom: 4 },
  title: { color: appColors.text.primary, fontSize: 20, fontWeight: '700' },
  scroll: { flex: 1 },
  content: { padding: 16, gap: 12 },
  notice: { color: appColors.text.secondary, textAlign: 'center', paddingVertical: 32 },
  card: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.alt,
    padding: 12,
    gap: 4,
  },
  cardTitle: { color: appColors.text.primary, fontSize: 13, fontWeight: '700', marginBottom: 4 },
  statusRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  dot: { width: 8, height: 8, borderRadius: 4 },
  statusLabel: { color: appColors.text.secondary, fontSize: 12, width: 64 },
  statusValue: { color: appColors.text.primary, fontSize: 12, flex: 1 },
  meterRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 2,
    minHeight: 42,
  },
  meterBar: { width: 4, borderRadius: 2, backgroundColor: '#A1D493' },
  meterBarHot: { backgroundColor: appColors.accent.gold },
  actionRow: { flexDirection: 'row', gap: 12 },
  actionDisabled: { opacity: 0.4 },
  action: {
    flex: 1,
    height: 44,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.alt,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionText: { color: appColors.text.primary, fontWeight: '600', fontSize: 13 },
  mono: {
    color: appColors.text.secondary,
    fontSize: 11,
    fontFamily: Platform.select({ ios: 'Menlo', android: 'monospace', default: 'monospace' }),
  },
  mutedMono: { opacity: 0.45 },
  tapVerdict: { color: appColors.text.primary, fontWeight: '700' },
  matchedSignal: { color: '#A1D493' },
  wrongSignal: { color: '#e08a8a' },
  muted: { color: appColors.text.secondary, fontSize: 12 },
  footer: {
    padding: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: appColors.border.strong,
  },
  closeButton: {
    alignSelf: 'stretch',
    height: 48,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.alt,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeText: {
    color: appColors.text.secondary,
    fontWeight: '700',
    fontSize: 12,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
  },
});
